<template>
  <a-modal
    :bodyStyle="{ 'padding': '0px 12px 24px 12px' }"
    :title="$t('timeConfig.setTimeTitle')"
    :okText="$t('timeConfig.saveBtn')"
    :cancelText="$t('timeConfig.cancelBtn')"
    :visible="true"
    :maskClosable="false"
    @cancel="handleCancel"
  >  
    <h4 class="table-title">
      {{`${$t('timeConfig.setTimeTitle')} - ${configSource.name}`}}
    </h4>

    <a-table
      bordered
      size="middle"
      :pagination="false"
      :columns="tableColumns"
      :data-source="tableData"
    >
      <template slot="week" slot-scope="text, record">
        <span>{{$t(text)}}</span>
      </template>

      <template slot="times" slot-scope="text, record">
        <div class="table-row">
          <TimeBar 
            :times="record.times"
            :pickerVisible="record.pickerVisible"
            @changePickerVisible="show => handleChangePickerVisible(show, record)"            
            @changeTimes="times => handleChangeTimes(times, record)"
          />
        </div>
      </template>

    </a-table>
    
    <template slot="footer">
      <div class="footer-area">
        <div class="area-left">
          <a-button @click="handleEmpty">{{$t('timeConfig.emptyBtn')}}</a-button>
          <a-button @click="handleReset">{{$t('timeConfig.resetBtn')}}</a-button>
        </div>
        
        <div class="area-right">
          <a-form :form="form" class="save-as-form" layout="inline" @submit.prevent="handleSaveAt">
            <a-form-item 
              style="text-align:left;max-width:350px" 
              :validateStatus="validateStatus"
              :help="$t(validateRes)"
            >
              <a-input
                style="width: 350px"
                :placeholder="$t('timeConfig.saveAsNameTxt')"
                v-decorator="[
                  'name',
                  { 
                    rules: [{ validator: validateNameRepeat }],
                    validateTrigger: ['change']
                  }
                ]"
              />
            </a-form-item>
            <a-form-item>
              <a-button 
                type="primary" 
                html-type="submit" 
                :loading="nameValidating || saveAtLoading"
              >{{nameValidating ? $t('timeConfig.nameValidating') : $t('timeConfig.saveAsBtn')}}</a-button>
            </a-form-item>
          </a-form>
          <div class="save-btn">
            <a-button type="primary" :loading="confirmLoading" @click="handleConfirmConfig">{{$t('timeConfig.saveBtn')}}</a-button>
          </div>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { debounce, numberToLenStr } from '@/utils/utils'
import { 
  getAllTimeConfig,
  createTimeConfig,
  modifyTimeConfig
} from '@/api/timeConfig';
import TimeBar from './TimeBar';
import { mapState } from 'vuex';

const WEEK_LANG_STR = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default {
  name: 'SetTimeModal',
  components: { TimeBar },
  props: {
    configSource: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      newConfig: {
        id: '',
        name: '',
        times: { times1: [], times2: [], times3: [], times4: [], times5: [], times6: [], times7: [] }
      },
      // -------------- 另存为 --------------
      form: this.$form.createForm(this),
      validateStatus: null,
      validateRes: '',

      nameValidating: false,
      repeatNameId: '',

      saveAtLoading: false,

      // -------------- 时间表 --------------
      tableColumns: [],
      tableData: [],
      
      // 提交等待
      confirmLoading: false
    }
  },
  computed: {
    ...mapState(['lang'])
  },
  watch: {
    lang: function (lang) {
      this.tableColumns = this.createTableColumns()
    }
  }, 
  created() {
    this.copyConfigSource()
    this.createTableColumns()
    this.creatTableData(this.newConfig)
  },
  methods: {    
    // 深度拷贝 configSource
    copyConfigSource() {
      this.newConfig.id = this.configSource.id
      this.newConfig.name = this.configSource.name

      const times = this.configSource.times
      Object.keys(times).forEach(key => {
        this.newConfig.times[key] = []
        times[key].forEach((time, index) => {
          this.newConfig.times[key][index] = { ...time }
        })
      })
    },

    // 构造表头
    createTableColumns() {
      this.tableColumns = [{
        title: this.$t('timeConfig.tableWeekTime'),
        dataIndex: 'week',
        width: 92,
        className: 'time-column',
        scopedSlots: { customRender: 'week' }
      }]
      for (let i = 0; i < 24; i++) {
        const hour = numberToLenStr(i, 2)
        const nextHour = numberToLenStr((i + 1) % 24, 2)
        const timeColumn = {
          title: `${hour}:00\n~\n${nextHour}:00`,
          dataIndex: hour,
          width: 60,
          className: 'time-column',
          scopedSlots: i === 0 ? { customRender: 'times' } : undefined
        }
        this.tableColumns.push(timeColumn)
      }
    },
    // 构造表格数据
    creatTableData(config) {
      const newTableData = []
      for (let i = 0; i < 7; i++) {
        const key = `times${i + 1}`
        let temp = {
          key: key,
          week: 'timeConfig.' + WEEK_LANG_STR[i],
          times: config.times[key],
          pickerVisible: false
        }
        newTableData.push(temp)
      }
      this.tableData = [].concat(newTableData)
    },

    // TimeBar 组件中时间段选择器显示状态改变
    handleChangePickerVisible(show, record) {
      if (show) {
        this.tableData.forEach(row => {
          if (row.key !== record.key) {
            row.pickerVisible = false
          } else {
            row.pickerVisible = true
          }
        })
      } else {
        this.tableData.forEach(row => { row.pickerVisible = false })
      }
    },
    // TimeBar 组件中时间条改变
    handleChangeTimes(times, record) {
      this.newConfig.times[record.key] = times
      record.times = times
    },
    
    // ------------------ 另存为 -------------------
    // 校验另存为时的配置名称输入
    async validateNameRepeat(rule, value, callback) {
      // console.log(rule, value)
      this.repeatNameId = ''
      this.validateStatus = null
      this.validateRes = ''
      if (!value) {
        this.validateStatus = 'error'
        this.validateRes = 'timeConfig.saveAsNameBlank'
      } else if (value.length > 20) {
        this.validateStatus = 'error'
        this.validateRes = 'timeConfig.saveAsNameWarning'
      } else {
        this.debunceGetTimeConfigByName({ name: value })
      }
      callback()
    },
    // 防抖方式校验名称是否重复
    debunceGetTimeConfigByName: debounce(async function(params) {
      try {
        if (this.validateStatus === 'error') return

        this.nameValidating = true
        const { data } = await getAllTimeConfig(params)
        this.nameValidating = false

        if (data.collection.length) {
          this.repeatNameId = data.collection[0].id
          this.validateStatus = 'warning'
          this.validateRes = 'timeConfig.nameRepeatWarning'
        }
      } catch (error) {
        this.nameValidating = false
      }
    }, 300),
    handleSaveAt() {
      this.form.validateFields(async (err, values) => {
        // console.log('-handleSaveAt-', err, values, this.repeatNameId)
        try {
          const request = this.repeatNameId ? modifyTimeConfig : createTimeConfig
          this.saveAtLoading = true
          await request({
            id: this.repeatNameId || null,
            name: values.name,
            times: this.newConfig.times
          })
          this.saveAtLoading = false

          this.$notification.success({
            message: this.$t('timeConfig.saveAsSuccess'),
            duration: 2
          })
          this.$emit('close', { refreshList: true })
        } catch (error) {
          this.saveAtLoading = false
        }
      })
    },
    // ---------------------------------------------

    // 提交时间配置内容
    async handleConfirmConfig() {
      try {
        this.confirmLoading = true
        await modifyTimeConfig(this.newConfig)
        this.confirmLoading = false
        
        this.$notification.success({
          message: this.$t('timeConfig.saveSuccess'),
          duration: 2
        })
        this.$emit('close', { refreshList: true })
      } catch (error) {
        this.confirmLoading = false
      }
    },
    // 关闭模态框
    handleCancel() {
      this.$emit('close')
    },
    
    // 清空
    handleEmpty() {
      const times = this.newConfig.times
      Object.keys(times).forEach(key => { times[key] = [] })
      this.creatTableData(this.newConfig)
    },
    // 重置
    handleReset() {
      this.copyConfigSource()
      this.creatTableData(this.newConfig)
    }
  }
}
</script>

<style lang="less" scoped>
.table-title{
  margin: 0;
  padding: 15px 0;
  font-size: 16px;
  color: #6c6c6c
}
.table-title:before{
  content: "";
  margin-right: 10px;
  border: 2px solid #1890ff;
  border-radius: 5px;
}

::v-deep .ant-modal{
  width: 1554px;
  min-width: 1554px;
  user-select: none
}
::v-deep th.time-column,
::v-deep td.time-column{
  text-align: center !important;
  padding: 12px, 4px !important;
}

.table-row{
  height: 46px;
  position: absolute;
  left: 90px;
  right: 0px;
  margin-top: -23px;
}

.footer-area{
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .area-left{
    flex-grow: 1;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }
  .area-right{
    flex-grow: 1;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    .save-btn{
      border-left: 1px solid #d9d9d9;
      margin: 4px 0;
      padding-left:  16px;
    }
  }
}
</style>
